import { Box, Popper, alpha, ClickAwayListener, Stack } from '@mui/material';
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useBundleTranslation from 'i18n';
import IconMi from 'components/common/icon/IconMi';
import { useQuery } from '@tanstack/react-query';
import { instance } from 'api/api';
import { useConfigConstantValue } from 'hooks/useConfigConstant';

interface RichTextMentionMenuProps {
    updateMentionData: (data: any) => any[];
    quill: any;
    richTextContainerRef: any;
    onChangeStateMentionMenu: (state: boolean) => void;
}
export default function RichTextMentionMenu(props: RichTextMentionMenuProps) {
    const { t } = useBundleTranslation(['components/common/rich-text']);
    const { quill, richTextContainerRef, onChangeStateMentionMenu, updateMentionData } = props;
    const [searchString, setSearchString] = useState<string>('');
    const maxMentionMenuItems = useConfigConstantValue('GLOSSARY_MENTION_SUGGESTION_LIMIT') ?? 10;

    const { data: mentionSearchData, isLoading: mentionSearchIsLoading } = useQuery<any, Error>({
        queryKey: ['mentionSearchData', 'searchQuery' + searchString],
        queryFn: async () => {
            const response: any = await instance.get('data/admin/glossary-setup/search', {
                params: {
                    q: searchString,
                    limit: maxMentionMenuItems,
                },
            });
            if (response?.data?.results) {
                return response.data.results;
            }
            return [];
        },
        staleTime: 15 * 60 * 1000, //15 minutes
    });

    //is show mention menu
    const [isActiveMentionMenu, setIsActiveMentionMenu] = useState<boolean>(false);
    const isActiveMentionMenuRef = useRef<boolean>(false);
    const updateIsActiveMentionMenu = useCallback((isShow: boolean) => {
        isActiveMentionMenuRef.current = isShow;
        setIsActiveMentionMenu(isShow);
    }, []);

    //selected mention item index
    const [activeMentionMenuItemIndex, setActiveMentionMenuItemIndex] = useState<number>(0);
    const activeMentionMenuItemIndexRef = useRef<number>(0);
    const updateMentionMenuItemIndex = useCallback((index: number) => {
        activeMentionMenuItemIndexRef.current = index;
        setActiveMentionMenuItemIndex(index);
    }, []);

    const lastShowItemIndexRef = useRef<number>(0);

    const mentionSearchStart = useRef<number>(0);
    const filteredMenuItems = useRef<any[]>([]);

    const closestScrollContainer = useRef<any>(null);

    const popperRef = useRef<any>(null);
    const positionMentionMenuRef = useRef<{
        x: number;
        y: number;
        height: number;
        placement: string;
        maxHeight: string;
    }>({
        x: 0,
        y: 0,
        height: 0,
        placement: 'top-start',
        maxHeight: 'none',
    });
    const mentionMenuResultContainerRef = useRef<any>(null);

    const fillMenuItemsByLimit = (menuItems: any[]) => {
        const limit = maxMentionMenuItems;

        if (menuItems.length === 0) return menuItems;

        const totalItems = menuItems.reduce((acc: number, group: any) => acc + group.items.length, 0);
        if (totalItems <= limit) return menuItems;

        const quotas = menuItems.map(() => 0);
        let remainingSlots = limit;

        while (remainingSlots > 0) {
            const groupsWithRoom = menuItems
                .map((group, i) => ({ index: i, room: group.items.length - quotas[i] }))
                .filter((g) => g.room > 0);

            if (groupsWithRoom.length === 0) break;

            const perGroup = Math.floor(remainingSlots / groupsWithRoom.length);

            if (perGroup === 0) {
                for (let i = 0; i < remainingSlots; i++) {
                    quotas[groupsWithRoom[i].index]++;
                }
                break;
            }

            for (const g of groupsWithRoom) {
                const add = Math.min(perGroup, g.room);
                quotas[g.index] += add;
                remainingSlots -= add;
            }
        }

        return menuItems.map((group, i) => ({
            ...group,
            items: group.items.slice(0, quotas[i]),
        }));
    };

    filteredMenuItems.current = useMemo(() => {
        let menuItems: any[] = [];

        if (mentionSearchIsLoading) {
            lastShowItemIndexRef.current = 0;
            return [];
        }
        if ((mentionSearchData ?? []).length > 0) {
            menuItems = Object.values(
                mentionSearchData.reduce((acc: any, item: any) => {
                    if (!acc[item.type]) acc[item.type] = { groupLabel: item.type, items: [] };
                    acc[item.type].items.push({
                        type: item.type,
                        key: item.id,
                        label: item.name,
                        desc: '',
                        icon: null,
                    });
                    return acc;
                }, {}),
            );
            menuItems = fillMenuItemsByLimit(menuItems);
        }

        //filter by search string
        /*menuItems = menuItems.map((group) => {
            return {
                ...group,
                items: group.items.filter((item: any) => {
                    return (
                        searchString == '' ||
                        item.label.toLowerCase().includes(searchString.toLowerCase()) ||
                        item.desc.toLowerCase().includes(searchString.toLowerCase())
                    );
                }),
            };
        });*/

        let indexItem = -1;
        //set index for elements and remove empty groups
        menuItems = menuItems
            .filter((group: any) => group.items.length > 0)
            .map((group) => {
                return {
                    ...group,
                    items: group.items.map((item: any) => {
                        indexItem++;
                        return { ...item, index: indexItem };
                    }),
                };
            });

        if (menuItems.length > 0) {
            lastShowItemIndexRef.current = indexItem;
        } else {
            lastShowItemIndexRef.current = 0;
        }

        return menuItems;
    }, [searchString, mentionSearchData, mentionSearchIsLoading]);

    useEffect(() => {
        if ((mentionSearchData ?? []).length > 0) {
            updateMentionData(mentionSearchData);
        }
    }, [mentionSearchData]);

    const showMentionMenu = (index: number) => {
        mentionSearchStart.current = index;
        updateMentionMenuPosition();

        quill.formatText(index, 1, 'mention-query', true, 'silent');
        updateIsActiveMentionMenu(true);
    };
    const updateMentionMenuPosition = () => {
        const index = mentionSearchStart.current + 1;
        const mentionLineBounds = quill.getBounds(index);
        const containerBounds = quill.container.getBoundingClientRect();
        const mentionQueryElBorder = 2;

        if (
            positionMentionMenuRef.current.y == containerBounds.top + mentionLineBounds.top &&
            positionMentionMenuRef.current.x == containerBounds.left + mentionLineBounds.left
        ) {
            return false;
        }

        const menuWindowIndent = 16;
        const placeBefore = containerBounds.top + mentionLineBounds.top;
        const placeAfter = window.innerHeight - placeBefore - mentionLineBounds.height;

        positionMentionMenuRef.current = {
            x: containerBounds.left + mentionLineBounds.left,
            y: containerBounds.top + mentionLineBounds.top,
            height: mentionLineBounds.height + mentionQueryElBorder,
            placement: placeBefore >= placeAfter ? 'top-start' : 'bottom-start',
            maxHeight: (placeBefore >= placeAfter ? placeBefore : placeAfter) - menuWindowIndent + 'px',
        };
        if (popperRef.current) {
            popperRef.current.update();
        }
    };

    const hideMentionMenu = useCallback(() => {
        updateIsActiveMentionMenu(false);
        let [leaf, offset] = quill.getLeaf(mentionSearchStart.current + 1);
        if (leaf) {
            quill.formatText(mentionSearchStart.current, leaf.domNode.length, 'mention-query', false, 'silent');
        }
    }, []);

    const addMentionItem = (mentionItem: any) => {
        updateIsActiveMentionMenu(false);
        let [leaf, offset] = quill.getLeaf(mentionSearchStart.current + 1);
        quill.deleteText(mentionSearchStart.current, leaf.domNode.length, 'mention-query', false, 'silent');

        quill.insertEmbed(
            mentionSearchStart.current,
            'mention-item',
            { text: mentionItem.label, id: mentionItem.key, type: mentionItem.type },
            'user',
        );

        setTimeout(() => {
            quill.setSelection(mentionSearchStart.current + 1, 'silent');
        }, 10);
    };

    const textChangeHandler = (delta: any, oldDelta: any, source: string) => {
        const retain = delta.ops[0]?.retain ?? 0;
        const insertIndex = retain ? 1 : 0;

        const typeSymbol =
            ((!retain && delta.ops.length == 1) || (retain && delta.ops.length == 2)) && delta.ops[insertIndex]?.insert;
        if (!isActiveMentionMenuRef.current) {
            //clear mention styles if exist on change (like undo / redo)
            const isMentionQueryExist = Object.values(delta.ops).some((item: any) => {
                return item?.attributes?.['mention-query'];
            });
            if (isMentionQueryExist) {
                quill.formatText(0, quill.getLength(), 'mention-query', false, 'silent');
            }

            if (typeSymbol && delta.ops[insertIndex]?.insert == '@') {
                showMentionMenu(retain);
            }
        } else {
            const start = mentionSearchStart.current + 1;
            let [leaf, offset] = quill.getLeaf(start);
            if (quill.getFormat()['mention-query']) {
                updateMentionMenuPosition();
                setSearchString(leaf.domNode.length > 1 ? leaf.text.substring(1).toLowerCase().trim() : '');
            } else {
                hideMentionMenu();
            }
        }
    };

    const selectionChangeHandler = (
        range: null | { index: number; length: number },
        oldRange: null | { index: number; length: number },
        source: string,
    ) => {
        if (isActiveMentionMenuRef.current && range) {
            if (range.length == 0) {
                //if cursor caret move out of mention line then hide menu
                if (!quill.getFormat()['mention-query']) {
                    hideMentionMenu();
                }
            } else {
                //if selection few symbols then hide menu
                hideMentionMenu();
            }
        }
    };

    useEffect(() => {
        quill.on('text-change', textChangeHandler);
        quill.on('selection-change', selectionChangeHandler);

        return () => {
            quill.off('text-change', textChangeHandler);
            quill.off('selection-change', selectionChangeHandler);
        };
    }, []);

    const scrollToActiveItem = useCallback(() => {
        const activeIndex = activeMentionMenuItemIndexRef.current;
        const container: HTMLElement | null = mentionMenuResultContainerRef.current;
        const element: HTMLElement | null = container
            ? container.querySelector(`.mention-menu-result-item--${activeIndex}`)
            : null;
        if (container && element) {
            if (activeIndex == 0) {
                //scroll to top of the container (first element)
                container.scrollTo({
                    top: 0,
                    behavior: 'instant',
                });
            } else if (element.getBoundingClientRect().top < container.getBoundingClientRect().top) {
                const previousEl = element.previousElementSibling;
                if (previousEl && previousEl.classList.contains('mention-menu-result-group-heading')) {
                    previousEl.scrollIntoView({
                        behavior: 'instant',
                        block: 'nearest',
                    });
                } else {
                    element.scrollIntoView({
                        behavior: 'instant',
                        block: 'nearest',
                    });
                }
            } else if (element.getBoundingClientRect().bottom > container.getBoundingClientRect().bottom) {
                //scroll down, element over bottom of the container
                element.scrollIntoView({
                    behavior: 'instant',
                    block: 'nearest',
                });
            }
        }
    }, []);

    const keyPressHandler = useCallback((event: KeyboardEvent) => {
        switch (event.key) {
            case 'Enter':
                if (filteredMenuItems.current.length > 0) {
                    const resultItem = filteredMenuItems.current
                        .flatMap((g) => g.items || [])
                        .find((it) => it.index === activeMentionMenuItemIndexRef.current);
                    if (resultItem) {
                        addMentionItem(resultItem);
                    } else {
                        hideMentionMenu();
                    }
                } else {
                    hideMentionMenu();
                }
                event.preventDefault();
                break;
            case 'ArrowUp':
                if (activeMentionMenuItemIndexRef.current !== 0) {
                    updateMentionMenuItemIndex(activeMentionMenuItemIndexRef.current - 1);
                } else if (lastShowItemIndexRef.current > 0) {
                    updateMentionMenuItemIndex(lastShowItemIndexRef.current);
                }
                scrollToActiveItem();
                event.preventDefault();
                break;
            case 'ArrowDown':
                if (activeMentionMenuItemIndexRef.current !== lastShowItemIndexRef.current) {
                    updateMentionMenuItemIndex(activeMentionMenuItemIndexRef.current + 1);
                } else if (lastShowItemIndexRef.current > 0) {
                    updateMentionMenuItemIndex(0);
                }
                scrollToActiveItem();
                event.preventDefault();
                break;
        }
    }, []);

    useEffect(() => {
        onChangeStateMentionMenu(isActiveMentionMenuRef.current);
        if (isActiveMentionMenuRef.current) {
            quill.root.addEventListener('keydown', keyPressHandler);
            window.addEventListener('scroll', hideMentionMenu);
            window.addEventListener('resize', hideMentionMenu);
            if (richTextContainerRef.current) {
                closestScrollContainer.current = richTextContainerRef.current.closest('.scroll-content-container');
                if (closestScrollContainer.current) {
                    closestScrollContainer.current.addEventListener('scroll', hideMentionMenu);
                }
            }
        } else {
            quill.root.removeEventListener('keydown', keyPressHandler);
            window.removeEventListener('scroll', hideMentionMenu);
            window.removeEventListener('resize', hideMentionMenu);
            if (closestScrollContainer.current) {
                closestScrollContainer.current.removeEventListener('scroll', hideMentionMenu);
            }
            updateMentionMenuItemIndex(0);
            setSearchString('');
        }
    }, [isActiveMentionMenu]);

    return (
        <>
            <Popper
                sx={{ zIndex: (theme) => theme.zIndex.appBar + 1 }}
                placement={positionMentionMenuRef.current.placement as any}
                open={isActiveMentionMenu}
                popperRef={popperRef}
                anchorEl={{
                    getBoundingClientRect: () => {
                        return new DOMRect(
                            positionMentionMenuRef.current.x,
                            positionMentionMenuRef.current.y,
                            0,
                            positionMentionMenuRef.current.height,
                        );
                    },
                }}
            >
                <ClickAwayListener
                    onClickAway={(event: Event | React.SyntheticEvent) => {
                        hideMentionMenu();
                    }}
                >
                    <Box
                        sx={{
                            backgroundColor: 'background.default',
                            borderRadius: '4px',
                            boxShadow: (theme) => `0px 2px 8px 0px ${alpha(theme.palette.text.primary, 0.16)}`,
                            position: 'relative',
                            border: (theme) => `1px solid ${alpha(theme.palette.text.primary, 0.08)}`,
                            py: 0.5,
                            overflow: 'auto',
                            maxHeight: positionMentionMenuRef.current.maxHeight,
                        }}
                        ref={mentionMenuResultContainerRef}
                    >
                        <Stack
                            sx={{
                                width: '192px',
                                '.active': { backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.08) },
                            }}
                        >
                            {mentionSearchIsLoading ? (
                                <Stack
                                    direction={'row'}
                                    sx={{
                                        py: '5px',
                                        px: 1,
                                        cursor: 'default',
                                        userSelect: 'none',
                                        alignItems: 'center',
                                        opacity: 0.64,
                                    }}
                                    onClick={hideMentionMenu}
                                >
                                    <Stack
                                        direction={'row'}
                                        className={'anim-spin'}
                                        sx={{
                                            width: '16px',
                                            height: '16px',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            mr: 1,
                                            flexShrink: 0,
                                            fontSize: '16px',
                                        }}
                                    >
                                        <IconMi icon={'loader'} />
                                    </Stack>
                                    <Box>{t('loading___')}</Box>
                                </Stack>
                            ) : (
                                <>
                                    {filteredMenuItems.current.length > 0 ? (
                                        <>
                                            {filteredMenuItems.current.map((group: any, groupIndex: number) => {
                                                return group.items.map((item: any, itemIndex: number) => {
                                                    return (
                                                        <Fragment key={`${item.type}_${item.key}`}>
                                                            {itemIndex == 0 && (
                                                                <Box
                                                                    sx={{
                                                                        color: (theme) =>
                                                                            alpha(theme.palette.text.primary, 0.64),
                                                                        fontWeight: 600,
                                                                        fontSize: '10px',
                                                                        lineHeight: '16px',
                                                                        px: 1,
                                                                        py: '1px',
                                                                        userSelect: 'none',
                                                                        '&.mention-menu-result-group-heading--separated':
                                                                            {
                                                                                borderTop: (theme) =>
                                                                                    `1px solid ${alpha(theme.palette.text.primary, 0.16)}`,
                                                                                pt: '5px',
                                                                            },
                                                                    }}
                                                                    className={`mention-menu-result-group-heading ${
                                                                        groupIndex == 0
                                                                            ? 'mention-menu-result-group-heading--first'
                                                                            : 'mention-menu-result-group-heading--separated'
                                                                    }`}
                                                                >
                                                                    {t(`mention_menu.group.${group.groupLabel}`)}
                                                                </Box>
                                                            )}
                                                            <Stack
                                                                direction={'row'}
                                                                sx={{
                                                                    py: '5px',
                                                                    px: 1,
                                                                    cursor: 'default',
                                                                    userSelect: 'none',
                                                                }}
                                                                className={`${
                                                                    item.index == activeMentionMenuItemIndex
                                                                        ? 'active'
                                                                        : 'default'
                                                                } mention-menu-result-item--${item.index}`}
                                                                onMouseEnter={() => {
                                                                    updateMentionMenuItemIndex(item.index);
                                                                }}
                                                                onClick={() => {
                                                                    addMentionItem(item);
                                                                }}
                                                            >
                                                                {item.icon && (
                                                                    <Stack
                                                                        direction={'row'}
                                                                        sx={{
                                                                            width: '16px',
                                                                            height: '16px',
                                                                            alignItems: 'center',
                                                                            justifyContent: 'center',
                                                                            mt: 0.5,
                                                                            mr: 1,
                                                                            flexShrink: 0,
                                                                            fontSize: '16px',
                                                                        }}
                                                                    >
                                                                        <IconMi icon={item.icon} />
                                                                    </Stack>
                                                                )}

                                                                <Box sx={{ minWidth: 0 }}>
                                                                    <Box sx={{ wordWrap: 'break-word' }}>
                                                                        {item.label}
                                                                    </Box>
                                                                    {item.desc && (
                                                                        <Box
                                                                            sx={{
                                                                                color: (theme) =>
                                                                                    alpha(
                                                                                        theme.palette.text.primary,
                                                                                        0.64,
                                                                                    ),
                                                                            }}
                                                                        >
                                                                            {item.desc}
                                                                        </Box>
                                                                    )}
                                                                </Box>
                                                            </Stack>
                                                        </Fragment>
                                                    );
                                                });
                                            })}
                                        </>
                                    ) : (
                                        <Box
                                            className={'active'}
                                            sx={{ py: 1, px: 1.5, cursor: 'default', userSelect: 'none' }}
                                            onClick={hideMentionMenu}
                                        >
                                            {t('command_menu_empty_results')}{' '}
                                            <Box component={'span'} sx={{ opacity: 0.64 }}>
                                                ({t('command_menu_empty_results_dismiss')})
                                            </Box>
                                        </Box>
                                    )}
                                </>
                            )}
                        </Stack>
                    </Box>
                </ClickAwayListener>
            </Popper>
        </>
    );
}
